import { Button, Checkbox, toast } from "@devhop/ui";
import { useEffect, useMemo, useState } from "react";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { VehicleModelCodeDTO } from "../api/client";
import {
  useConfigurationCodeAssignmentsQuery,
  useSetConfigurationCodeAssignments,
} from "../api/queries";

type VehicleConfigurationCodeAssignmentsSectionProps = {
  configurationId: string;
};

function groupCodes(codes: VehicleModelCodeDTO[]) {
  return {
    specs: codes.filter((c) => c.codeType === "spec"),
    options: codes.filter((c) => c.codeType === "option"),
  };
}

function CodeCheckboxGroup({
  title,
  codes,
  selectedIds,
  disabled,
  onToggle,
}: {
  title: string;
  codes: VehicleModelCodeDTO[];
  selectedIds: Set<string>;
  disabled: boolean;
  onToggle: (codeId: string, checked: boolean) => void;
}) {
  if (codes.length === 0) return null;

  return (
    <div className="space-y-2">
      <p className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
        {title}
      </p>
      <div className="grid gap-2 sm:grid-cols-2">
        {codes.map((code) => (
          <label
            key={code.id}
            className="flex cursor-pointer items-start gap-2 rounded-lg border bg-background px-3 py-2"
          >
            <Checkbox
              checked={selectedIds.has(code.id)}
              disabled={disabled}
              onCheckedChange={(checked) =>
                onToggle(code.id, checked === true)
              }
            />
            <span className="min-w-0 flex-1">
              <span className="block font-mono text-xs">{code.code}</span>
              {code.label ? (
                <span className="block text-muted-foreground text-xs">
                  {code.label}
                </span>
              ) : null}
            </span>
          </label>
        ))}
      </div>
    </div>
  );
}

export function VehicleConfigurationCodeAssignmentsSection({
  configurationId,
}: VehicleConfigurationCodeAssignmentsSectionProps) {
  const canUpdate = useActionPermission(["catalog:update"]);
  const assignmentsQuery = useConfigurationCodeAssignmentsQuery(configurationId);
  const saveAssignments = useSetConfigurationCodeAssignments(configurationId);

  const modelCodes = assignmentsQuery.data?.modelCodes ?? [];
  const assignedIds = assignmentsQuery.data?.modelCodeIds ?? [];
  const { specs, options } = useMemo(
    () => groupCodes(modelCodes),
    [modelCodes],
  );

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setSelectedIds(new Set(assignedIds));
    setDirty(false);
  }, [assignedIds]);

  const toggleCode = (codeId: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(codeId);
      else next.delete(codeId);
      return next;
    });
    setDirty(true);
  };

  const selectAll = () => {
    setSelectedIds(new Set(modelCodes.map((c) => c.id)));
    setDirty(true);
  };

  const clearAll = () => {
    setSelectedIds(new Set());
    setDirty(true);
  };

  return (
    <section className="space-y-3 rounded-xl border bg-card p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="font-semibold text-base">
            Codes ສຳລັບ configuration ນີ້
          </h3>
          <p className="text-muted-foreground text-xs">
            ເລືອກຈາກຄລັງລະຫັດຂອງຮຸ່ນ — ແຕ່ລະ configuration ເລືອກໄດ້ຄົນລະຊຸດ
          </p>
        </div>
        {canUpdate && modelCodes.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            <Button type="button" size="sm" variant="outline" onClick={selectAll}>
              ເລືອກທັງໝົດ
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={clearAll}>
              ລ້າງ
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={!dirty || saveAssignments.isPending}
              onClick={async () => {
                try {
                  await saveAssignments.mutateAsync({
                    modelCodeIds: [...selectedIds],
                  });
                  setDirty(false);
                } catch {
                  toast.error("ບັນທຶກບໍ່ສຳເລັດ");
                }
              }}
            >
              ບັນທຶກ
            </Button>
          </div>
        ) : null}
      </div>

      {assignmentsQuery.isLoading ? (
        <p className="text-muted-foreground text-sm">ກຳລັງໂຫຼດ...</p>
      ) : modelCodes.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          ຍັງບໍ່ມີ code ໃນຄລັງຮຸ່ນ — ເພີ່ມທີ່ໜ້າລາຍລະອຽດຮຸ່ນກ່ອນ
        </p>
      ) : (
        <div className="space-y-4">
          <CodeCheckboxGroup
            title="Spec"
            codes={specs}
            selectedIds={selectedIds}
            disabled={!canUpdate || saveAssignments.isPending}
            onToggle={toggleCode}
          />
          <CodeCheckboxGroup
            title="Option"
            codes={options}
            selectedIds={selectedIds}
            disabled={!canUpdate || saveAssignments.isPending}
            onToggle={toggleCode}
          />
          <p className="text-muted-foreground text-xs">
            ເລືອກແລ້ວ {selectedIds.size} / {modelCodes.length} codes
          </p>
        </div>
      )}
    </section>
  );
}
